import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import '../../CSS/BillList.css';
import api from "../../api.js";

/* ════════ HELPERS ════════ */
function fmt(n) { return (Math.round((n || 0) * 100) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }
function fmtK(n) { return n >= 10000000 ? (n / 10000000).toFixed(2) + 'Cr' : n >= 100000 ? (n / 100000).toFixed(1) + 'L' : n >= 1000 ? (n / 1000).toFixed(0) + 'K' : n; }
function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}
function toISODate(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '';
  return dt.toISOString().slice(0, 10);
}
function stamp() { return new Date().toISOString().slice(0, 10).replace(/-/g, ''); }

const STATUS_LABELS = { pending: '⏳ Pending', confirmed: '✓ Confirmed', generated: '★ IRN Generated' };
const TOAST_ICONS = { ok: '✓', err: '✕', warn: '⚠', info: 'ℹ' };

let toastId = 0;

/* ════════════════════════════════════════════
   AUTOCOMPLETE (ported from ConsignmentBooking.jsx)
════════════════════════════════════════════ */
function AutoComplete({ options, value, onChange, placeholder = 'Search…', disabled = false }) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [focused, setFocused] = useState(-1);
  const [cursorMoved, setCursorMoved] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const optionRefs = useRef([]);

  const selected = options.find(o => String(o.value) === String(value));

  useEffect(() => {
    if (selected) setQuery(selected.label);
    else setQuery('');
  }, [value, selected]);

  const filtered = options.filter(
    o => o.value !== undefined && o.label != null && o.label.toLowerCase().includes((query || '').toLowerCase())
  );

  useEffect(() => {
    if (focused >= 0 && optionRefs.current[focused]) {
      try { optionRefs.current[focused].scrollIntoView({ block: 'nearest' }); } catch (err) { /* noop */ }
    }
  }, [focused]);

  function hl(text, q) {
    if (!q) return text;
    const i = text.toLowerCase().indexOf(q.toLowerCase());
    if (i < 0) return text;
    return (<>{text.slice(0, i)}<mark>{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>);
  }

  function choose(opt) {
    onChange(opt.value);
    setQuery(opt.label);
    setIsOpen(false);
    setFocused(-1);
    setCursorMoved(false);
  }

  function clear() {
    onChange('');
    setQuery('');
    setIsOpen(true);
    setFocused(-1);
  }

  useEffect(() => {
    function handle(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setIsOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  function handleKeyDown(e) {
    if (disabled) return;
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { setIsOpen(true); setCursorMoved(true); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault(); setCursorMoved(true);
      setFocused(f => Math.min(f + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault(); setCursorMoved(true);
      setFocused(f => Math.max(f - 1, 0));
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (cursorMoved && filtered.length > 0) {
        const target = filtered[focused] || filtered[0];
        if (target) choose(target);
      } else if (filtered.length === 1) {
        choose(filtered[0]);
      } else {
        setIsOpen(false);
      }
    }
  }

  return (
    <div className="billlist-ac" ref={wrapRef}>
      <div className={'billlist-ac-box' + (isOpen ? ' billlist-ac-open' : '') + (disabled ? ' billlist-ac-disabled' : '')}>
        <input
          ref={inputRef}
          className="billlist-ac-in"
          type="text"
          autoComplete="off"
          disabled={disabled}
          placeholder={placeholder}
          value={query}
          onFocus={() => { if (!disabled) { setIsOpen(true); setCursorMoved(false); } }}
          onClick={() => { if (!disabled && !isOpen) setIsOpen(true); }}
          onChange={e => { if (disabled) return; setQuery(e.target.value); setIsOpen(true); setFocused(-1); }}
          onKeyDown={handleKeyDown}
        />
        {value && !disabled && (
          <span className="billlist-ac-x" onMouseDown={e => { e.preventDefault(); clear(); }}>×</span>
        )}
        <span className="billlist-ac-chev" onMouseDown={e => { if (disabled) return; e.preventDefault(); setIsOpen(o => !o); }}>▾</span>
      </div>
      {isOpen && !disabled && (
        <div className="billlist-ac-drop">
          {filtered.length === 0 ? (
            <div className="billlist-ac-empty">No matches found</div>
          ) : (
            filtered.map((opt, i) => (
              <div
                key={opt.value}
                ref={el => { optionRefs.current[i] = el; }}
                className={'billlist-ac-opt' + (String(opt.value) === String(value) ? ' billlist-ac-sel' : '') + (i === focused ? ' billlist-ac-foc' : '')}
                onMouseDown={e => { e.preventDefault(); choose(opt); }}
                onMouseEnter={() => setFocused(i)}
              >
                {hl(opt.label, query)}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════
   MAP API ROW -> UI INVOICE SHAPE
   API (/get-bill-list) returns per row using .NET's default
   camelCase JSON serialization, e.g.:
   bill_No, bill_Date, party_Name, gstin, state_Name,
   taxable, igst, cgst, sgst, total, irN_Code, ack_No, ack_Date,
   bill_Status, bill_Accepted, bill_Confirmation_Status, isBillConfirmed,
   uploaded, eInvoice_Status, isEInvoiceUploaded
════════════════════════════════════════════ */
function mapBillRow(r) {
  const isConfirmed = !!r.isBillConfirmed;
  const isUploaded = !!r.isEInvoiceUploaded;
  let status = 'pending';
  if (isConfirmed && isUploaded) status = 'generated';
  else if (isConfirmed) status = 'confirmed';

  return {
    billId: r.bill_Id,       // hidden internal key — used only for confirm/generate calls, never rendered
    no: r.bill_No,
    date: toISODate(r.bill_Date),
    dateRaw: r.bill_Date,
    party: r.party_Name || '—',
    gstin: r.gstin || '—',
    stateName: r.state_Name || '—',
    taxable: Number(r.taxable) || 0,
    cgst: Number(r.cgst) || 0,
    sgst: Number(r.sgst) || 0,
    igst: Number(r.igst) || 0,
    total: Number(r.total) || 0,
    irn: r.irN_Code || null,
    ackNo: r.ack_No || null,
    ackDt: r.ack_Date || null,
    billStatus: r.bill_Status,
    isConfirmed,
    isUploaded,
    status,
  };
}

export default function BillList() {
  const [fromDate, setFromDate] = useState('2026-06-01');
  const [toDate, setToDate] = useState('2026-06-30');
  const [partyFilter, setPartyFilter] = useState('');   // party_id (from API)
  const [branchFilter, setBranchFilter] = useState(''); // Branch_id (from API)
  const [search, setSearch] = useState('');
  const [curTab, setCurTab] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [exportModal, setExportModal] = useState(null); // {title, sub, meta, json, filename}
  const [detailInvoice, setDetailInvoice] = useState(null);
  const [toasts, setToasts] = useState([]);

  /* ════════ BRANCH LIST (API) ════════ */
  const [branchList, setBranchList] = useState([]);
  const [branchListLoading, setBranchListLoading] = useState(true);
  const [branchListError, setBranchListError] = useState(null);

  useEffect(() => {
    setBranchListLoading(true);
    setBranchListError(null);
    api.get('/dropdown', { params: { mode: 'BLB' } })
      .then(res => setBranchList(Array.isArray(res.data) ? res.data : []))
      .catch(err => {
        console.error('Branch list fetch error:', err);
        setBranchListError('Failed to load branches');
      })
      .finally(() => setBranchListLoading(false));
  }, []);

  /* ════════ PARTY LIST — dependent on selected Branch (API) ════════ */
  const [partyList, setPartyList] = useState([]);
  const [partyListLoading, setPartyListLoading] = useState(false);
  const [partyListError, setPartyListError] = useState(null);

  useEffect(() => {
    // Reset party selection & list whenever branch changes
    setPartyFilter('');
    setPartyList([]);
    setPartyListError(null);

    if (!branchFilter) return; // no branch chosen yet — leave party list empty

    setPartyListLoading(true);
    api.get('/get-Billingpartylist', { params: { billingBranchId: branchFilter } })
      .then(res => setPartyList(Array.isArray(res.data) ? res.data : []))
      .catch(err => {
        console.error('Party list fetch error:', err);
        setPartyListError('Failed to load parties');
      })
      .finally(() => setPartyListLoading(false));
  }, [branchFilter]);

  /* ════════ BILL LIST (API) — driven by fromDate/toDate/branch/party ════════ */
  const [invoices, setInvoices] = useState([]);
  const [billListLoading, setBillListLoading] = useState(false);
  const [billListError, setBillListError] = useState(null);

  const fetchBills = useCallback(() => {
    if (!fromDate || !toDate) return;
    setBillListLoading(true);
    setBillListError(null);
    api.get('/get-bill-list', {
      params: {
        fromDate,
        toDate,
        branchId: branchFilter || undefined,
        partyId: partyFilter || undefined,
      },
    })
      .then(res => {
        const rows = Array.isArray(res.data?.data) ? res.data.data : [];
        setInvoices(rows.map(mapBillRow));
      })
      .catch(err => {
        console.error('Bill list fetch error:', err);
        setBillListError(err?.response?.data?.message || 'Failed to load bills');
        setInvoices([]);
      })
      .finally(() => setBillListLoading(false));
  }, [fromDate, toDate, branchFilter, partyFilter]);

  useEffect(() => { fetchBills(); }, [fetchBills]);

  const toast = useCallback((msg, type) => {
    const id = ++toastId;
    setToasts(t => [...t, { id, msg, type, leaving: false }]);
    setTimeout(() => {
      setToasts(t => t.map(x => x.id === id ? { ...x, leaving: true } : x));
      setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 320);
    }, 2900);
  }, []);

  /* ════════ CLIENT-SIDE REFINEMENT: SEARCH ONLY ════════
     Date / Branch / Party are already applied server-side via API params.
     Search (invoice no / GSTIN / party name) is refined here on the fetched set. */
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return invoices;
    return invoices.filter(i =>
      (i.no || '').toLowerCase().includes(q) ||
      (i.gstin || '').toLowerCase().includes(q) ||
      (i.party || '').toLowerCase().includes(q)
    );
  }, [invoices, search]);

  const tabFiltered = useMemo(() => curTab === 'all' ? filtered : filtered.filter(i => i.status === curTab), [filtered, curTab]);

  const selectedInvoices = useMemo(() => invoices.filter(i => selected.has(i.billId)), [invoices, selected]);

  /* ════════ SELECTION (keyed by billId — the hidden, stable primary key) ════════ */
  function toggleRow(billId, checked) {
    setSelected(prev => {
      const next = new Set(prev);
      if (checked) next.add(billId); else next.delete(billId);
      return next;
    });
  }
  function toggleAll(checked) {
    setSelected(prev => {
      const next = new Set(prev);
      if (checked) tabFiltered.forEach(i => next.add(i.billId));
      else tabFiltered.forEach(i => next.delete(i.billId));
      return next;
    });
  }
  function setTab(t) { setCurTab(t); setSelected(new Set()); }
  function clearFilters() { setSelected(new Set()); }

  /* ════════ CONFIRM (calls backend, then refetches) ════════
     POST /confirm-bill { billId }  ->  UPDATE Bill SET Bill_Accepted='Y', Accept_Date, Accepted_by WHERE Bill_Id=@billId */
  function confirmOne(billId, no) {
    api.post('/confirm-bill', { billId })
      .then(() => { toast('Invoice ' + no + ' confirmed for e-invoicing', 'ok'); fetchBills(); })
      .catch(err => {
        console.error('Confirm error:', err);
        toast(err?.response?.data?.message || 'Failed to confirm ' + no, 'err');
      });
  }
  function confirmSelected() {
    const targets = selectedInvoices.filter(i => i.status === 'pending');
    if (!targets.length) { toast('Select pending invoices to confirm', 'warn'); return; }
    Promise.allSettled(targets.map(i => api.post('/confirm-bill', { billId: i.billId })))
      .then(results => {
        const okCount = results.filter(r => r.status === 'fulfilled').length;
        const failCount = results.length - okCount;
        if (okCount) toast(okCount + ' invoice' + (okCount > 1 ? 's' : '') + ' confirmed — ready for e-invoice generation', 'ok');
        if (failCount) toast(failCount + ' invoice' + (failCount > 1 ? 's' : '') + ' failed to confirm', 'err');
        fetchBills();
        setSelected(new Set());
      });
  }

  /* ════════ GENERATE IRN (calls backend, then refetches) ════════
     NOTE: assumes a POST /generate-einvoice endpoint accepting { billId }.
     Update the URL/payload below to match your actual backend route once that endpoint exists. */
  function generateOne(billId, no) {
    const i = invoices.find(x => x.billId === billId);
    if (!i || i.status !== 'confirmed') { toast('Only confirmed invoices can be generated', 'warn'); return; }
    api.post('/generate-einvoice', { billId })
      .then(() => { toast('E-Invoice generated — IRN created for ' + no, 'ok'); fetchBills(); })
      .catch(err => {
        console.error('Generate error:', err);
        toast(err?.response?.data?.message || 'Failed to generate e-invoice for ' + no, 'err');
      });
  }
  function generateSelected() {
    const targets = selectedInvoices.filter(i => i.status === 'confirmed');
    if (!targets.length) { toast('Select confirmed invoices to generate', 'warn'); return; }
    Promise.allSettled(targets.map(i => api.post('/generate-einvoice', { billId: i.billId })))
      .then(results => {
        const okCount = results.filter(r => r.status === 'fulfilled').length;
        const failCount = results.length - okCount;
        if (okCount) toast(okCount + ' e-invoice' + (okCount > 1 ? 's' : '') + ' generated (IRN)', 'ok');
        if (failCount) toast(failCount + ' e-invoice' + (failCount > 1 ? 's' : '') + ' failed to generate', 'err');
        fetchBills();
        setSelected(new Set());
      });
  }

  /* ════════ EXPORT TARGET ════════ */
  function exportTargets() {
    const sel = selectedInvoices.filter(i => i.status === 'confirmed' || i.status === 'generated');
    if (sel.length) return sel;
    return filtered.filter(i => i.status === 'confirmed' || i.status === 'generated');
  }

  /* ════════ TALLY JSON ════════ */
  function exportTally() {
    const inv = exportTargets();
    if (!inv.length) { toast('No confirmed invoices to export', 'warn'); return; }
    const obj = {
      exportType: 'TALLY_SALES_VOUCHERS',
      generatedOn: new Date().toISOString(), voucherCount: inv.length,
      vouchers: inv.map(i => ({
        voucherType: 'Sales', date: (i.date || '').replace(/-/g, ''), voucherNumber: i.no,
        reference: i.no, partyLedgerName: i.party, partyGstin: i.gstin,
        placeOfSupply: i.stateName,
        isEInvoice: i.status === 'generated', irn: i.irn || null,
        ledgerEntries: [
          { ledgerName: i.party, amount: +i.total.toFixed(2), isDeemedPositive: true },
          { ledgerName: 'Freight & Logistics Income', amount: -(+i.taxable.toFixed(2)), isDeemedPositive: false },
          ...(i.igst > 0
            ? [{ ledgerName: 'Output IGST', amount: -(+i.igst.toFixed(2)), isDeemedPositive: false }]
            : [{ ledgerName: 'Output CGST', amount: -(+i.cgst.toFixed(2)), isDeemedPositive: false },
               { ledgerName: 'Output SGST', amount: -(+i.sgst.toFixed(2)), isDeemedPositive: false }]),
        ],
      })),
    };
    showExport('Tally Export (JSON)', 'Sales vouchers for Tally import',
      [['Vouchers', inv.length], ['Total', `₹${fmt(inv.reduce((a, b) => a + b.total, 0))}`], ['Format', 'Tally Sales']],
      obj, 'Tally_Export_' + stamp() + '.json');
  }

  /* ════════ GSTR-1 JSON ════════ */
  function exportGstr1() {
    const inv = exportTargets();
    if (!inv.length) { toast('No confirmed invoices to export', 'warn'); return; }
    const ft = toDate || (inv[inv.length - 1] && inv[inv.length - 1].date) || '';
    const fp = ft ? ft.slice(5, 7) + ft.slice(0, 4) : ''; // MMYYYY
    const byCtin = {};
    inv.forEach(i => {
      (byCtin[i.gstin] = byCtin[i.gstin] || []).push({
        inum: i.no, idt: (i.date || '').split('-').reverse().join('-'), val: +i.total.toFixed(2),
        rchrg: 'N', inv_typ: 'R',
        itms: [{ num: 1, itm_det: { txval: +i.taxable.toFixed(2), iamt: +i.igst.toFixed(2), camt: +i.cgst.toFixed(2), samt: +i.sgst.toFixed(2), csamt: 0 } }],
      });
    });
    const obj = {
      fp, gt: +inv.reduce((a, b) => a + b.total, 0).toFixed(2),
      cur_gt: +inv.reduce((a, b) => a + b.total, 0).toFixed(2),
      version: 'GST3.0.4',
      b2b: Object.keys(byCtin).map(ctin => ({ ctin, inv: byCtin[ctin] })),
    };
    showExport('GSTR-1 Export (JSON)', 'B2B invoices · return period ' + fp,
      [['Invoices', inv.length], ['B2B Parties', Object.keys(byCtin).length], ['Period', fp]],
      obj, 'GSTR1_' + fp + '_' + stamp() + '.json');
  }

  /* ════════ EXPORT MODAL ════════ */
  function showExport(title, sub, meta, obj, filename) {
    setExportModal({ title, sub, meta, json: JSON.stringify(obj, null, 2), filename });
  }
  function downloadCurrent() {
    if (!exportModal) return;
    const blob = new Blob([exportModal.json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = exportModal.filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
    toast('Downloaded ' + exportModal.filename, 'ok');
  }
  function copyJson() {
    if (!exportModal) return;
    navigator.clipboard?.writeText(exportModal.json).then(
      () => toast('JSON copied to clipboard', 'ok'),
      () => toast('Copy failed', 'err')
    );
  }

  /* ════════ STATS ════════ */
  const stats = useMemo(() => ({
    count: filtered.length,
    value: filtered.reduce((a, b) => a + b.total, 0),
    pending: filtered.filter(i => i.status === 'pending').length,
    confirmed: filtered.filter(i => i.status === 'confirmed').length,
    generated: filtered.filter(i => i.status === 'generated').length,
  }), [filtered]);

  const tabCounts = useMemo(() => ({
    all: filtered.length,
    pending: filtered.filter(i => i.status === 'pending').length,
    confirmed: filtered.filter(i => i.status === 'confirmed').length,
    generated: filtered.filter(i => i.status === 'generated').length,
  }), [filtered]);

  const footTotals = useMemo(() => tabFiltered.reduce((a, b) => ({
    tax: a.tax + b.taxable, cgst: a.cgst + b.cgst, sgst: a.sgst + b.sgst, igst: a.igst + b.igst, tot: a.tot + b.total,
  }), { tax: 0, cgst: 0, sgst: 0, igst: 0, tot: 0 }), [tabFiltered]);

  const allSelectedInTab = tabFiltered.length > 0 && tabFiltered.every(i => selected.has(i.billId));
  const canConfirm = selectedInvoices.some(i => i.status === 'pending');
  const canGenerate = selectedInvoices.some(i => i.status === 'confirmed');

  function rowAction(i) {
    if (i.status === 'pending') {
      return (
        <button className="billlist-mini-btn billlist-mb-confirm" onClick={() => confirmOne(i.billId, i.no)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg> Confirm
        </button>
      );
    }
    if (i.status === 'confirmed') {
      return (
        <button className="billlist-mini-btn billlist-mb-gen" onClick={() => generateOne(i.billId, i.no)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></svg> Generate
        </button>
      );
    }
    return <button className="billlist-mini-btn billlist-mb-view" onClick={() => setDetailInvoice(i)}>View IRN</button>;
  }

  function detailRows(i) {
    const rows = [
      ['Invoice Date', fmtDate(i.date)], ['Party', i.party], ['Buyer GSTIN', i.gstin],
      ['Place of Supply', i.stateName],
      ['Taxable', '₹' + fmt(i.taxable)],
      ...(i.igst > 0 ? [['IGST', '₹' + fmt(i.igst)]] : [['CGST', '₹' + fmt(i.cgst)], ['SGST', '₹' + fmt(i.sgst)]]),
      ['Total', '₹' + fmt(i.total)], ['Status', i.status.toUpperCase()],
    ];
    if (i.irn) { rows.push(['IRN', i.irn]); rows.push(['Ack No.', i.ackNo]); rows.push(['Ack Date', i.ackDt]); }
    return rows;
  }

  return (
    <div className="billlist-root">
      <div className="billlist-page">
        <div className="billlist-pg-row">
          <div className="billlist-pg-title">
            <h1>Bill List &amp; E-Invoice Confirmation</h1>
            <p>Review invoices for a period, confirm them for e-invoicing, generate IRNs, and export the confirmed list to Tally &amp; GSTR-1.</p>
          </div>
        </div>

        <div className="billlist-stat-row">
          <div className="billlist-stat-box billlist-navy"><div className="billlist-sb-val">{stats.count}</div><div className="billlist-sb-lbl">Invoices</div></div>
          <div className="billlist-stat-box"><div className="billlist-sb-val">₹{fmtK(stats.value)}</div><div className="billlist-sb-lbl">Total Value</div></div>
          <div className="billlist-stat-box billlist-amber"><div className="billlist-sb-val">{stats.pending}</div><div className="billlist-sb-lbl">Pending Confirm</div></div>
          <div className="billlist-stat-box billlist-blue"><div className="billlist-sb-val">{stats.confirmed}</div><div className="billlist-sb-lbl">Confirmed</div></div>
          <div className="billlist-stat-box billlist-green"><div className="billlist-sb-val">{stats.generated}</div><div className="billlist-sb-lbl">IRN Generated</div></div>
        </div>

        {/* FILTERS */}
        <div className="billlist-card billlist-card-visible">
          <div className="billlist-band billlist-navy-band">
            <div className="billlist-band-icon"><svg viewBox="0 0 24 24" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg></div>
            <div><div className="billlist-band-title">Period &amp; Filters</div></div>
          </div>
          <div className="billlist-card-body">
            <div className="billlist-filters">
              <div className="billlist-field">
                <label>From Date</label>
                <input type="date" value={fromDate} onChange={e => { setFromDate(e.target.value); clearFilters(); }} />
              </div>
              <div className="billlist-field">
                <label>To Date</label>
                <input type="date" value={toDate} onChange={e => { setToDate(e.target.value); clearFilters(); }} />
              </div>
               <div className="billlist-field">
                <label>Branch</label>
                <AutoComplete
                  options={branchList.map(b => ({ value: b.Branch_id, label: b.Branch_Name }))}
                  value={branchFilter}
                  onChange={v => { setBranchFilter(v); clearFilters(); }}
                  placeholder={branchListLoading ? 'Loading branches…' : 'All Branches'}
                  disabled={branchListLoading}
                />
                {branchListError && (
                  <span style={{ fontSize: 11, color: 'var(--red)', marginTop: 2 }}>⚠ {branchListError}</span>
                )}
              </div>
              <div className="billlist-field">
                <label>Party</label>
                <AutoComplete
                  options={partyList.map(p => ({ value: p.party_id, label: p.party_Name }))}
                  value={partyFilter}
                  onChange={v => { setPartyFilter(v); clearFilters(); }}
                  placeholder={
                    !branchFilter ? 'All Parties'
                    : partyListLoading ? 'Loading parties…'
                    : partyListError ? 'Error loading parties'
                    : 'All Parties'
                  }
                  disabled={!branchFilter || partyListLoading}
                />
                {partyListError && (
                  <span style={{ fontSize: 11, color: 'var(--red)', marginTop: 2 }}>⚠ {partyListError}</span>
                )}
              </div>

              <div className="billlist-field">
                <label>Search</label>
                <input type="text" placeholder="Invoice no / GSTIN / Party…" value={search} onChange={e => { setSearch(e.target.value); clearFilters(); }} />
              </div>
            </div>
          </div>
        </div>

        {/* TABS */}
        <div className="billlist-tabs">
          {['all', 'pending', 'confirmed', 'generated'].map(t => (
            <button key={t} className={'billlist-tab' + (curTab === t ? ' billlist-active' : '')} onClick={() => setTab(t)}>
              {t === 'all' ? 'All' : t === 'pending' ? 'Pending' : t === 'confirmed' ? 'Confirmed' : 'IRN Generated'}{' '}
              <span className="billlist-cnt">{tabCounts[t]}</span>
            </button>
          ))}
        </div>

        {/* LIST */}
        <div className="billlist-card">
          <div className="billlist-band billlist-navy-band">
            <div className="billlist-band-icon"><svg viewBox="0 0 24 24" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg></div>
            <div>
              <div className="billlist-band-title">Invoices</div>
              <div className="billlist-band-sub">{fromDate ? fmtDate(fromDate) : '—'}  to  {toDate ? fmtDate(toDate) : '—'}</div>
            </div>
            <div className="billlist-band-r"><span className="billlist-band-badge">{tabFiltered.length}</span></div>
          </div>

          <div className="billlist-action-bar">
            <span className="billlist-ab-sel"><span>{selectedInvoices.length}</span> selected</span>
            <button className="billlist-btn billlist-btn-primary billlist-btn-sm" onClick={confirmSelected} disabled={!canConfirm}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg> Confirm for E-Invoicing
            </button>
            <button className="billlist-btn billlist-btn-teal billlist-btn-sm" onClick={generateSelected} disabled={!canGenerate}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></svg> Generate E-Invoice (IRN)
            </button>
            <div className="billlist-ab-spacer"></div>
            <button className="billlist-btn billlist-btn-ghost billlist-btn-sm" onClick={fetchBills} disabled={billListLoading}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M20.49 9A9 9 0 005.6 5.6L1 10m22 4l-4.6 4.4A9 9 0 013.51 15" /></svg> {billListLoading ? 'Refreshing…' : 'Refresh'}
            </button>
            <button className="billlist-btn billlist-btn-purple billlist-btn-sm" onClick={exportTally}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg> Export Tally JSON
            </button>
            <button className="billlist-btn billlist-btn-navy billlist-btn-sm" onClick={exportGstr1}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg> Export GSTR-1 JSON
            </button>
          </div>

          <div className="billlist-tbl-scroll">
            <table className="billlist-bills">
              <thead>
                <tr>
                  <th className="billlist-c"><input type="checkbox" className="billlist-chk" checked={allSelectedInTab} onChange={e => toggleAll(e.target.checked)} /></th>
                  <th>Invoice No.</th><th>Date</th><th>Party</th><th>GST No.</th><th>Place</th>
                  <th className="billlist-r">Taxable</th><th className="billlist-r">CGST</th><th className="billlist-r">SGST</th><th className="billlist-r">IGST</th><th className="billlist-r">Total</th>
                  <th>IRN No.</th><th>Ack No.</th><th>Ack Date</th><th className="billlist-c">Status</th><th className="billlist-c">Action</th>
                </tr>
              </thead>
              <tbody>
                {billListLoading && (
                  <tr><td colSpan={16}>
                    <div className="billlist-empty">
                      <div style={{ fontWeight: 700, color: 'var(--slate)' }}>Loading bills…</div>
                    </div>
                  </td></tr>
                )}
                {!billListLoading && billListError && (
                  <tr><td colSpan={16}>
                    <div className="billlist-empty">
                      <div className="billlist-empty-ico">⚠</div>
                      <div style={{ fontWeight: 700, color: 'var(--red)' }}>{billListError}</div>
                    </div>
                  </td></tr>
                )}
                {!billListLoading && !billListError && tabFiltered.length === 0 && (
                  <tr><td colSpan={16}>
                    <div className="billlist-empty">
                      <div className="billlist-empty-ico">🧾</div>
                      <div style={{ fontWeight: 700, color: 'var(--slate)' }}>No invoices for this period / filter</div>
                    </div>
                  </td></tr>
                )}
                {!billListLoading && !billListError && tabFiltered.map(i => {
                  const sel = selected.has(i.billId);
                  return (
                    <tr key={i.billId} className={sel ? 'billlist-sel' : ''}>
                      <td className="billlist-c"><input type="checkbox" className="billlist-chk" checked={sel} onChange={e => toggleRow(i.billId, e.target.checked)} /></td>
                      <td><span className="billlist-inv-no">{i.no}</span></td>
                      <td style={{ whiteSpace: 'nowrap', color: 'var(--slate)' }}>{fmtDate(i.date)}</td>
                      <td><div className="billlist-party-nm">{i.party}</div></td>
                      <td><span className="billlist-mono" style={{ fontSize: '11px' }}>{i.gstin}</span></td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {i.stateName}<br />
                        <span style={{ fontSize: '10px', color: 'var(--muted)' }}>{i.igst ? 'IGST' : 'CGST+SGST'}</span>
                      </td>
                      <td className="billlist-r"><span className="billlist-mono">₹{fmt(i.taxable)}</span></td>
                      <td className="billlist-r"><span className="billlist-mono" style={{ color: i.cgst ? 'var(--teal)' : 'var(--muted)' }}>{i.cgst ? '₹' + fmt(i.cgst) : '—'}</span></td>
                      <td className="billlist-r"><span className="billlist-mono" style={{ color: i.sgst ? 'var(--teal)' : 'var(--muted)' }}>{i.sgst ? '₹' + fmt(i.sgst) : '—'}</span></td>
                      <td className="billlist-r"><span className="billlist-mono" style={{ color: i.igst ? 'var(--purple)' : 'var(--muted)' }}>{i.igst ? '₹' + fmt(i.igst) : '—'}</span></td>
                      <td className="billlist-r"><span className="billlist-mono">₹{fmt(i.total)}</span></td>
                      <td>{i.irn ? <span className="billlist-irn-cell" title={i.irn} onClick={() => setDetailInvoice(i)}>{i.irn.slice(0, 18)}…</span> : <span style={{ color: 'var(--muted)' }}>—</span>}</td>
                      <td><span className="billlist-mono" style={{ fontSize: '11px' }}>{i.ackNo || <span style={{ color: 'var(--muted)' }}>—</span>}</span></td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: '11px', color: 'var(--slate)' }}>{i.ackDt ? fmtDate(i.ackDt) : <span style={{ color: 'var(--muted)' }}>—</span>}</td>
                      <td className="billlist-c"><span className={'billlist-pill billlist-pill-' + i.status}>{STATUS_LABELS[i.status]}</span></td>
                      <td className="billlist-c"><div className="billlist-row-act">{rowAction(i)}</div></td>
                    </tr>
                  );
                })}
              </tbody>
              {!billListLoading && !billListError && tabFiltered.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={6} className="billlist-lbl">Total · {tabFiltered.length} invoices</td>
                    <td className="billlist-r">₹ {fmt(footTotals.tax)}</td>
                    <td className="billlist-r">₹ {fmt(footTotals.cgst)}</td>
                    <td className="billlist-r">₹ {fmt(footTotals.sgst)}</td>
                    <td className="billlist-r">₹ {fmt(footTotals.igst)}</td>
                    <td className="billlist-r">₹ {fmt(footTotals.tot)}</td>
                    <td colSpan={5}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>

      {/* EXPORT MODAL */}
      <div className={'billlist-modal-overlay' + (exportModal ? ' billlist-show' : '')} onMouseDown={e => { if (e.target === e.currentTarget) setExportModal(null); }}>
        <div className="billlist-modal">
          <div className="billlist-modal-head">
            <div className="billlist-mh-ico"><svg viewBox="0 0 24 24" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg></div>
            <div><div className="billlist-mh-title">{exportModal?.title || 'Export'}</div><div className="billlist-mh-sub">{exportModal?.sub || '—'}</div></div>
            <button className="billlist-modal-x" onClick={() => setExportModal(null)}>✕</button>
          </div>
          <div className="billlist-modal-body">
            <div className="billlist-json-meta">
              {exportModal?.meta.map((m, idx) => <div key={idx}>{m[0]}: <b>{m[1]}</b></div>)}
            </div>
            <pre className="billlist-json-view">{exportModal?.json}</pre>
          </div>
          <div className="billlist-modal-foot">
            <button className="billlist-btn billlist-btn-ghost" onClick={() => setExportModal(null)}>Close</button>
            <button className="billlist-btn billlist-btn-ghost" onClick={copyJson}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></svg> Copy
            </button>
            <button className="billlist-btn billlist-btn-green" onClick={downloadCurrent}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg> Download JSON
            </button>
          </div>
        </div>
      </div>

      {/* INVOICE DETAIL MODAL */}
      <div className={'billlist-modal-overlay' + (detailInvoice ? ' billlist-show' : '')} onMouseDown={e => { if (e.target === e.currentTarget) setDetailInvoice(null); }}>
        <div className="billlist-modal" style={{ maxWidth: '480px' }}>
          <div className="billlist-modal-head">
            <div className="billlist-mh-ico"><svg viewBox="0 0 24 24" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg></div>
            <div><div className="billlist-mh-title">{detailInvoice?.no || 'Invoice'}</div><div className="billlist-mh-sub">{detailInvoice?.party || '—'}</div></div>
            <button className="billlist-modal-x" onClick={() => setDetailInvoice(null)}>✕</button>
          </div>
          <div className="billlist-modal-body">
            {detailInvoice && detailRows(detailInvoice).map((r, idx) => (
              <div className="billlist-det-row" key={idx}>
                <span className="billlist-det-k">{r[0]}</span>
                <span className="billlist-det-v" style={{ wordBreak: 'break-all', maxWidth: '62%' }}>{r[1]}</span>
              </div>
            ))}
          </div>
          <div className="billlist-modal-foot"><button className="billlist-btn billlist-btn-ghost" onClick={() => setDetailInvoice(null)}>Close</button></div>
        </div>
      </div>

      <div className="billlist-toast-wrap">
        {toasts.map(t => (
          <div key={t.id} className={'billlist-toast billlist-' + (t.type || 'info') + (t.leaving ? ' billlist-leaving' : '')}>
            <span style={{ fontSize: '15px' }}>{TOAST_ICONS[t.type] || 'ℹ'}</span>{t.msg}
          </div>
        ))}
      </div>
    </div>
  );
}
